import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { baasSupabase as supabase } from '@/integrations/supabase/baasClient';
import { useBaaSAuth } from '@/contexts/BaaSAuthContext';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface PartnerNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

const TYPE_ICON: Record<string, string> = {
  info: 'ℹ️', ticket: '🎫', alert: '🚨', system: '⚙️', doc_request: '📄', sweep: '🔄', compliance: '🛡️',
};

export function BaaSNotificationBell() {
  const navigate = useNavigate();
  const { baasUser, user } = useBaaSAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<PartnerNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const partnerId = baasUser?.partnerId;

  const fetchNotifications = useCallback(async () => {
    if (!partnerId) return;
    const { data } = await (supabase as any).from('partner_notifications').select('*').eq('partner_id', partnerId).order('created_at', { ascending: false }).limit(40);
    const list = (data || []) as PartnerNotification[];
    setNotifications(list);
    setUnreadCount(list.filter(n => !n.read_at).length);
  }, [partnerId]);

  useEffect(() => {
    fetchNotifications();
    if (!partnerId) return;
    const ch = supabase.channel(`partner-notifs-${partnerId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'partner_notifications' }, () => fetchNotifications())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [partnerId, fetchNotifications]);

  const markRead = async (n: PartnerNotification) => {
    if (n.read_at) return;
    await (supabase as any).from('partner_notifications').update({ read_at: new Date().toISOString() }).eq('id', n.id);
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read_at);
    if (unread.length === 0) return;
    const now = new Date().toISOString();
    const ids = unread.map(n => n.id);
    // Batch update — one call for all unread
    await (supabase as any)
      .from('partner_notifications')
      .update({ read_at: now })
      .in('id', ids);
    setNotifications(prev => prev.map(x => ({ ...x, read_at: x.read_at || now })));
    setUnreadCount(0);
  };

  const handleClick = (n: PartnerNotification) => {
    markRead(n);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="h-4 w-4 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <h4 className="text-sm font-semibold">Notifications</h4>
            {unreadCount > 0 && <Badge className="text-[10px] h-4 px-1.5">{unreadCount}</Badge>}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="h-6 text-[10px] gap-1" onClick={markAllRead}>
                <CheckCheck className="w-3 h-3" /> Read all
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen(false)}><X className="h-3 w-3" /></Button>
          </div>
        </div>
        <ScrollArea className="max-h-[380px]">
          {notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-10" />
              <p className="text-xs text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {notifications.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={cn(
                    'w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex gap-3',
                    !n.read_at && 'bg-primary/5 border-l-2 border-l-primary'
                  )}
                >
                  <span className="text-lg shrink-0">{TYPE_ICON[n.type] || '🔔'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-xs leading-snug', !n.read_at && 'font-semibold')}>{n.title}</p>
                    {n.body && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[9px] text-muted-foreground mt-1">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</p>
                  </div>
                  {!n.read_at && <div className="w-2 h-2 rounded-full bg-primary mt-1 shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
